import React, { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useAuth } from '../hooks/useAuth'
import { API_BASE_URL } from '../utils/api'
import { PageContent } from '../components/Layout'
import { getGreetingQuote } from '../utils/greetingQuotes'
import DashboardGrid from '../components/DashboardGrid'
import AnnouncementModal from '../components/AnnouncementModal'

interface Announcement {
  id: number
  title: string
  content: string
  type: 'feature' | 'improvement' | 'bug_fix' | 'maintenance'
  created_at: string
  created_by: string
}

const Dashboard: React.FC = () => {
  const { t } = useTranslation()
  const { user, isAdmin } = useAuth()

  const [unreadAnnouncement, setUnreadAnnouncement] = useState<Announcement | null>(null)
  const [showAnnouncementModal, setShowAnnouncementModal] = useState(false)
  const [hasCheckedAnnouncement, setHasCheckedAnnouncement] = useState(false)

  // Check for unread announcements once per session
  useEffect(() => {
    if (!hasCheckedAnnouncement) {
      fetchLatestUnreadAnnouncement()
      setHasCheckedAnnouncement(true)
    }
  }, [])

  const fetchLatestUnreadAnnouncement = async () => {
    try {
      const token = localStorage.getItem('token')
      if (!token) return

      const response = await fetch(`${API_BASE_URL}/api/announcements/unread/latest`, {
        headers: { 'Authorization': `Bearer ${token}` }
      })

      if (response.ok) {
        const data = await response.json()
        if (data.announcement) {
          setUnreadAnnouncement(data.announcement)
          setShowAnnouncementModal(true)
        }
      }
    } catch {
      // Non-critical — silently fail
    }
  }

  const handleCloseAnnouncementModal = async () => {
    setShowAnnouncementModal(false)

    if (unreadAnnouncement) {
      try {
        const token = localStorage.getItem('token')
        if (!token) {
          setUnreadAnnouncement(null)
          return
        }

        await fetch(`${API_BASE_URL}/api/announcements/${unreadAnnouncement.id}/read`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        })
      } catch {
        // Non-critical
      }
    }

    setUnreadAnnouncement(null)
  }

  const greetingQuote = getGreetingQuote(user?.firstName, t)

  if (isAdmin) {
    return (
      <PageContent className="bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 min-h-screen -m-6 p-6">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">{t('dashboard.adminTitle')}</h1>
          <p className="text-gray-600 dark:text-gray-400 mt-2">
            {t('dashboard.adminDescription')}
          </p>
        </div>

        <DashboardGrid userId={Number(user?.id) || 0} designTheme="gradient" />

        {showAnnouncementModal && unreadAnnouncement && (
          <AnnouncementModal
            announcement={unreadAnnouncement}
            onClose={handleCloseAnnouncementModal}
          />
        )}
      </PageContent>
    )
  }

  return (
    <PageContent>
      <div className="mb-4 sm:mb-6">
        <div className="flex items-center justify-between">
          <div className="flex-1 min-w-0">
            <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold text-gray-900 dark:text-gray-100 mb-1 sm:mb-2 leading-tight">
              {greetingQuote.text}
            </h1>
          </div>
        </div>
      </div>

      <DashboardGrid userId={Number(user?.id) || 0} designTheme="gradient" />

      {showAnnouncementModal && unreadAnnouncement && (
        <AnnouncementModal
          announcement={unreadAnnouncement}
          onClose={handleCloseAnnouncementModal}
        />
      )}
    </PageContent>
  )
}

export default Dashboard
